// Barnes-Hut quadtree para Coulomb O(n log n).
import type { GraphNode } from './types.js';

interface QNode {
  cx: number; // center of mass x
  cy: number;
  mass: number; // sum of "sizes" usados como massa proxy
  // bounding box
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  body?: GraphNode; // leaf
  children?: [QNode?, QNode?, QNode?, QNode?]; // NW, NE, SW, SE
}

function makeNode(x0: number, y0: number, x1: number, y1: number): QNode {
  return { cx: 0, cy: 0, mass: 0, x0, y0, x1, y1 };
}

function quadrantIndex(node: QNode, x: number, y: number): 0 | 1 | 2 | 3 {
  const midX = (node.x0 + node.x1) / 2;
  const midY = (node.y0 + node.y1) / 2;
  if (x < midX) return y < midY ? 0 : 2;
  return y < midY ? 1 : 3;
}

function childBounds(node: QNode, q: 0 | 1 | 2 | 3): [number, number, number, number] {
  const midX = (node.x0 + node.x1) / 2;
  const midY = (node.y0 + node.y1) / 2;
  switch (q) {
    case 0:
      return [node.x0, node.y0, midX, midY];
    case 1:
      return [midX, node.y0, node.x1, midY];
    case 2:
      return [node.x0, midY, midX, node.y1];
    case 3:
      return [midX, midY, node.x1, node.y1];
  }
}

function insert(node: QNode, body: GraphNode): void {
  const mass = Math.max(body.size || 10, 10);

  if (!node.body && !node.children) {
    node.body = body;
    node.cx = body.x;
    node.cy = body.y;
    node.mass = mass;
    return;
  }

  if (node.body && !node.children) {
    // Subdividir
    const existing = node.body;
    
    // Evita stack overflow quando nós estão exatamente na mesma posição
    if (existing.x === body.x && existing.y === body.y) {
      body.x += (Math.random() - 0.5) * 0.1 || 0.01;
      body.y += (Math.random() - 0.5) * 0.1 || 0.01;
    }
    
    node.body = undefined;
    node.children = [undefined, undefined, undefined, undefined];
    const eq = quadrantIndex(node, existing.x, existing.y);
    const [ex0, ey0, ex1, ey1] = childBounds(node, eq);
    node.children[eq] = makeNode(ex0, ey0, ex1, ey1);
    insert(node.children[eq]!, existing);
  }

  // Acumula centro de massa.
  const totalMass = node.mass + mass;
  node.cx = (node.cx * node.mass + body.x * mass) / totalMass;
  node.cy = (node.cy * node.mass + body.y * mass) / totalMass;
  node.mass = totalMass;

  const q = quadrantIndex(node, body.x, body.y);
  if (!node.children![q]) {
    const [cx0, cy0, cx1, cy1] = childBounds(node, q);
    node.children![q] = makeNode(cx0, cy0, cx1, cy1);
  }
  insert(node.children![q]!, body);
}

export function buildQuadtree(nodes: GraphNode[]): QNode | null {
  if (nodes.length === 0) return null;
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const n of nodes) {
    if (n.x < x0) x0 = n.x;
    if (n.y < y0) y0 = n.y;
    if (n.x > x1) x1 = n.x;
    if (n.y > y1) y1 = n.y;
  }
  // Padding
  const pad = 10;
  x0 -= pad;
  y0 -= pad;
  x1 += pad;
  y1 += pad;
  // Bounding box quadrado
  const side = Math.max(x1 - x0, y1 - y0);
  const root = makeNode(x0, y0, x0 + side, y0 + side);
  for (const n of nodes) insert(root, n);
  return root;
}

// Acumula força em (ax, ay) para o body `target` usando aproximação Barnes-Hut com `theta`.
export function applyCoulomb(
  root: QNode | null,
  target: GraphNode,
  coulombStrength: number,
  coulombMaxForce: number,
  theta: number,
  dt: number,
): { ax: number; ay: number } {
  let ax = 0,
    ay = 0;
  if (!root) return { ax, ay };

  const stack: QNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop()!;
    const dx = target.x - node.cx;
    const dy = target.y - node.cy;
    const distSq = dx * dx + dy * dy + 0.5;
    const dist = Math.sqrt(distSq);
    const size = node.x1 - node.x0;

    // Critério Barnes-Hut: se (size / dist) < theta, trata como ponto.
    const isLeaf = node.body && !node.children;
    if (isLeaf) {
      if (node.body === target) continue;
      const force = Math.min((coulombStrength * node.mass) / (Math.max(node.body!.size, 10) * distSq), coulombMaxForce);
      ax += (dx / dist) * force * dt;
      ay += (dy / dist) * force * dt;
      continue;
    }

    if (size / dist < theta) {
      // Aproximação por centro de massa.
      const force = Math.min((coulombStrength * node.mass) / (10 * distSq), coulombMaxForce);
      ax += (dx / dist) * force * dt;
      ay += (dy / dist) * force * dt;
    } else {
      // Desce filhos.
      if (node.children) {
        for (const c of node.children) if (c) stack.push(c);
      }
    }
  }

  return { ax, ay };
}
